import { useAddToTrash } from "@/api/trash";
import MediaLibrary, { ImageFormData } from "@/components/images/MediaLibrary";
import ConfirmNavigation from "@/components/navigation/ConfirmNavigation";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useFormAutosave } from "@/hooks/useAutosave";
import { TypeOptionPosition } from "@/lib/image";
import {
  isSheetRevisionEntityType,
  SheetRevisionEntityType,
} from "@/api/sheet-revision/sheet-revision";
import { SheetHistoryDrawer } from "@/components/sheet-history/SheetHistoryDrawer";
import { capitalizeFirstLetter, WithPartial } from "@/lib/utils";
import { ChevronRightIcon } from "lucide-react";
import { cloneElement, isValidElement, ReactElement, ReactNode, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import {
  DisplayModeContextProvider,
  useDisplayModeState,
} from "@/context/DisplayModeContext";
import { FormSheetContext } from "@/context/FormSheetContext";
import FormHeader, { FormHeaderProps } from "./FormHeader";
import {
  FormHeadingMetadata,
  FormHeadingMetadataProps,
} from "./FormHeadingMetadata";

export type FormLayoutProps<T extends FieldValues> = Omit<
  WithPartial<FormHeaderProps, "onAddToTrash">,
  "backLabel"
> &
  Omit<FormHeadingMetadataProps<T>, "onClick"> & {
    type: TypeOptionPosition;
    /** When set, MediaLibrary position options use this key; document `type` stays for trash, history, breadcrumbs. */
    mediaLibraryPositionType?: TypeOptionPosition;
    id: string;
    children?: ReactNode;
    /**
     * Simplified ("Essentiel") body for this sheet. When provided and the user
     * selected the Essentiel mode, it replaces the tabbed `children` (Pro view).
     * Sheets that don't provide it always render the Pro view.
     */
    simplifiedContent?: ReactNode;
    invalidateOnChange?: () => void;
    computeImageName?: (data?: ImageFormData) => string;
    embedAttachmentOnSubmit?: boolean;
  /** Enables field minimize/expand for the sheet (form-fields.{formKey}). */
  formKey?: string;
  /** When true, disables the "Save and publish" action in the header. */
  publishDisabled?: boolean;
};

export const FormLayout = <T extends FieldValues>({
  children,
  simplifiedContent,
  onSave,
  linkOverview,
  reference,
  title,
  subtitle,
  picturesData,
  id,
  type,
  mediaLibraryPositionType,
  onAddToTrash,
  publicationStatus,
  form,
  print,
  relativePosition,
  invalidateOnChange,
  computeImageName,
  embedAttachmentOnSubmit = false,
  keyStatus,
  formKey,
  publishDisabled,
  duplicate,
  badges,
  referenceExtra,
}: FormLayoutProps<T> & { invalidateOnchange?: () => void }) => {
  const [imageMode, setImageMode] = useState<boolean>(false);
  const trashAdder = useAddToTrash();
  const { user, instanceData } = useAuth();
  const isReader = user?.role === "reader" ? true : false;

  // Per-instance default (new instances): open sheets that support the
  // simplified view in "essentiel" until the user picks a mode. Gated on
  // `simplifiedContent` so it only applies where an Essentiel view exists
  // (the artwork sheet today). Only a default — the user's choice wins.
  const supportsSimplified = Boolean(simplifiedContent);
  const instanceDefaultMode =
    supportsSimplified && instanceData?.defaultArtworkViewEssentiel
      ? "essentiel"
      : undefined;

  // Display mode (Essentiel / Pro) is per-user, per sheet type (formKey).
  const { mode: displayMode, setMode: setDisplayMode } = useDisplayModeState(
    formKey,
    instanceDefaultMode,
  );
  const canSimplify = Boolean(formKey) && Boolean(simplifiedContent);
  const isSimplified = canSimplify && displayMode === "essentiel";

  const { t } = useTranslation();
  const mediaPositionType = mediaLibraryPositionType ?? type;
  const sheetLabelKey = `fiche${capitalizeFirstLetter(type)}`;
  const sheetLabel = t(sheetLabelKey);

  useFormAutosave(form, () => onSave(), isReader);

  const handleAddToTrash = () => {
    if (publicationStatus === "bin") {
      toast({
        title: t("documentAlreadyInTrashToastTitle"),
        variant: "success",
        description: t("documentAlreadyInTrashToastDescription"),
      });
    } else {
      trashAdder.mutate(
        { documentId: id, documentType: type },
        {
          onSuccess: () => {
            toast({
              title: t("documentAddedToTrashToastTitle"),
              variant: "success",
              description: t("documentAddedToTrashToastDescription"),
            });
            form.reset(undefined, { keepValues: true });
            onAddToTrash?.();
          },
        },
      );
    }
  };

  const headerPrint =
    print && isValidElement(print)
      ? cloneElement(print as ReactElement<{ menuItemTrigger?: boolean }>, {
          menuItemTrigger: !isReader,
        })
      : print;

  // Essentiel header: icon + "Exporter" label + tooltip instead of the bare printer icon.
  const headingExport =
    print && isValidElement(print)
      ? cloneElement(print as ReactElement<{ labeledTrigger?: boolean }>, {
          labeledTrigger: true,
        })
      : print;

  const sheetBody = (
    <>
      <FormHeader
        onAddToTrash={handleAddToTrash}
        addToTrashDisabled={publicationStatus === "bin" || trashAdder.isPending}
        title={title}
        backLabel={t(`retourDashboardDepuis${capitalizeFirstLetter(type)}`)}
        onSave={onSave}
        linkOverview={linkOverview}
        sheetHistory={
          isSheetRevisionEntityType(type as string) ? (
            <SheetHistoryDrawer
              entityType={type as SheetRevisionEntityType}
              entityId={id}
              menuItemTrigger
              onRestoreSuccess={invalidateOnChange}
            />
          ) : undefined
        }
        print={headerPrint}
        duplicate={duplicate}
        relativePosition={relativePosition}
        publicationStatus={publicationStatus}
        publishDisabled={publishDisabled}
      />
      {imageMode ? (
        <div className="max-w-screen-2xl mx-auto px-8 mt-[70px]">
          <Breadcrumb className="text-left flex items-center gap-1 mb-4">
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => setImageMode(false)}
                className="text-gray-600 text-sm cursor-pointer"
              >
                {title ?? reference}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <ChevronRightIcon className="w-3 h-3 text-gray-300" />
            <BreadcrumbItem>
              <p className="text-primary font-[600] text-sm">
                {t("thumbnailManageImages_action")}
              </p>
            </BreadcrumbItem>
          </Breadcrumb>
          {mediaPositionType && (
            <MediaLibrary
              computeImageName={computeImageName}
              id={id}
              invalidateOnChange={invalidateOnChange}
              type={mediaPositionType}
              embedAttachmentOnSubmit={embedAttachmentOnSubmit}
              setImageMode={setImageMode}
              simplifiedDrawer={isSimplified}
            />
          )}
        </div>
      ) : (
        <>
          <FormHeadingMetadata
            sheetLabel={sheetLabel}
            reference={reference}
            title={title}
            form={form}
            subtitle={subtitle}
            picturesData={picturesData}
            publicationStatus={publicationStatus}
            onClick={() => setImageMode(true)}
            keyStatus={keyStatus}
            showSaveButton={isSimplified && !isReader}
            hideStatus={isSimplified}
            onSave={onSave}
            publishDisabled={publishDisabled}
            exportAction={headingExport}
            badges={badges}
            referenceExtra={referenceExtra}
          />
          <Separator />
          <ConfirmNavigation
            isBlocked={
              form.formState.isDirty &&
              Object.keys(form.formState.dirtyFields).length > 0
            }
          />
          {isSimplified ? simplifiedContent : children}
        </>
      )}
    </>
  );

  const sheetContextValue = {
    hasPictures: Boolean(picturesData),
    openImageMode: picturesData ? () => setImageMode(true) : undefined,
  };

  const withVisibility = formKey ? (
    <FormFieldVisibilityProvider formKey={formKey} forceAllVisible={isSimplified}>
      {sheetBody}
    </FormFieldVisibilityProvider>
  ) : (
    sheetBody
  );

  return (
    <FormSheetContext.Provider value={sheetContextValue}>
      <DisplayModeContextProvider
        mode={displayMode}
        setMode={setDisplayMode}
        formKey={canSimplify ? formKey : undefined}
      >
        {withVisibility}
      </DisplayModeContextProvider>
    </FormSheetContext.Provider>
  );
};
